import { useSelector } from "react-redux";

import { getPackages, getPackagesList } from "ee/selectors/packageSelectors";
import { getCurrentAppWorkspace } from "ee/selectors/selectedWorkspaceSelectors";
import { hasCreateModulePermission } from "ee/utils/permissionHelpers";
import {
  CANNOT_CONVERT_TAGGED_MODULE_WARN,
  CANNOT_CREATE_MODULE_WARN,
  CONVERT_ENTITY_UNPUBLISHED_CHANGES_WARN,
  createMessage,
} from "ee/constants/messages";
import { PACKAGE_TYPE } from "ee/constants/PackageConstants";

export type ConvertPackageList = ReturnType<
  typeof usePackageListToConvertEntity
>;

function usePackageListToConvertEntity() {
  const packagesList = useSelector(getPackagesList);
  const currentWorkspace = useSelector(getCurrentAppWorkspace);
  const consumedPackages = useSelector(getPackages);

  const currentWorkspacePackages = packagesList
    .filter((pkg) => pkg.workspaceId === currentWorkspace.id)
    .filter((pkg) => pkg.type !== PACKAGE_TYPE.UI)
    .map((pkg) => {
      const hasUnpublishedChanges =
        Date.parse(pkg.modifiedAt) - Date.parse(pkg.lastPublishedAt) > 0;
      const canCreateModule = hasCreateModulePermission(pkg.userPermissions);

      const consumedPkg = Object.values(consumedPackages ?? {}).find(
        (p) => p.originPackageId === pkg.id,
      );
      const isTaggedVersion =
        !!consumedPkg?.activeVersion && consumedPkg?.activeVersion !== "latest";

      let disabledTooltipText;

      if (hasUnpublishedChanges) {
        disabledTooltipText = createMessage(
          CONVERT_ENTITY_UNPUBLISHED_CHANGES_WARN,
        );
      }

      if (!canCreateModule) {
        disabledTooltipText = createMessage(CANNOT_CREATE_MODULE_WARN);
      }

      if (isTaggedVersion) {
        disabledTooltipText = createMessage(CANNOT_CONVERT_TAGGED_MODULE_WARN);
      }

      return {
        ...pkg,
        disabledTooltipText,
        isDisabled:
          !canCreateModule || hasUnpublishedChanges || isTaggedVersion,
      };
    });

  return currentWorkspacePackages;
}

export default usePackageListToConvertEntity;
